import React from 'react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';

import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Container,
  Flex,
  H1,
  H2,
  H4,
  HeaderInfo,
  HeaderInfoElement,
  A,
  Icon,
  styled,
} from '@erecruit/ui-kit';

import { Intl } from '@erecruit/core';
import { Authorize } from '@erecruit/modules';
import { AppPermissions } from '@erecruit/types';

import { Insightful } from '../../../types';
import {
  PositionRecordProps,
  PositionRecordParams,
  PositionInfoSections,
} from '../types';
import { MATCH_STATUSES_TRANSLATIONS } from '../helpers';

const { FormattedMessage } = Intl;

const StyledIcon = styled(Icon)({
  position: 'relative',
  top: 3,
  right: 4,
  marginRight: 10,
});

export type PositionHeaderProps = Required<
  Pick<PositionRecordProps, 'isClosingPosition'>
> &
  Insightful & {
    handleClose(): void;
    infoSections: PositionInfoSections;
  };

const PositionHeader = ({
  handleClose,
  isClosingPosition,
  isInsight,
  infoSections,
}: PositionHeaderProps) => {
  const history = useHistory();
  const routeMatch = useRouteMatch<PositionRecordParams>();

  const { id } = routeMatch.params;

  return (
    <Container padding={[4, 8]} gutter={4} background="pageSecondary">
      <Flex justify="space-between">
        <Breadcrumb>
          {isInsight && !!routeMatch.params.matchStatus && (
            <StyledIcon type="arrow-left-b" onClick={() => history.goBack()} />
          )}
          {!isInsight && (
            <BreadcrumbItem>
              <Link to={`/positions?positionType=All`}>
                <FormattedMessage
                  id={messageIds.vms.positionRecord.linkToPositions}
                />
              </Link>
            </BreadcrumbItem>
          )}
          <BreadcrumbItem>
            {isInsight && !!routeMatch.params.matchStatus ? (
              <A onClick={() => history.goBack()} inline weight={700}>
                {`${infoSections.aboutPosition.name}`}
              </A>
            ) : (
              <React.Fragment>
                <H1 inline>{infoSections.aboutPosition.name}</H1>
                <H2 inline padding={[0, 0, 0, 1]}>{`(${id})`}</H2>
              </React.Fragment>
            )}
          </BreadcrumbItem>

          {!!routeMatch.params.matchStatus && (
            <BreadcrumbItem>
              <H1 inline>
                {MATCH_STATUSES_TRANSLATIONS[routeMatch.params.matchStatus]}
              </H1>
            </BreadcrumbItem>
          )}
        </Breadcrumb>
        <Authorize permission={AppPermissions.ClosePosition}>
          {infoSections.aboutPosition.status?.id !== 0 ? (
            <Button
              loading={isClosingPosition}
              type="primary"
              size="small"
              onClick={handleClose}
            >
              <H4 color="inherit">
                <FormattedMessage
                  id={messageIds.vms.positionRecord.closePosition.buttonText}
                />
              </H4>
            </Button>
          ) : null}
        </Authorize>
      </Flex>
      <HeaderInfo>
        <HeaderInfoElement
          tooltip={
            <FormattedMessage
              id={messageIds.vms.positionRecord.positionStatus}
            />
          }
          label={infoSections.aboutPosition.status?.name}
          icon="positions"
        />
        <HeaderInfoElement
          tooltip={
            <FormattedMessage id={messageIds.vms.positionRecord.company} />
          }
          label={infoSections.aboutPosition.company?.name}
          icon="company"
        />
        <HeaderInfoElement
          tooltip={
            <FormattedMessage
              id={messageIds.vms.positionRecord.hiringManager}
            />
          }
          label={infoSections.aboutPosition.contact?.name}
          icon="user"
        />
      </HeaderInfo>
    </Container>
  );
};

export default PositionHeader;
